import type { MissionId } from "~domain/sites/models/mission-model.ts";
import type {
  SiteAutodiagDeviceDiagnosis,
  SiteAutodiagDeviceDiagnostic,
  SiteAutodiagDeviceInfo,
  SiteAutodiagDeviceMemo,
  SiteAutodiagDeviceProgramming,
  SiteAutodiagDeviceSensor,
  SiteAutodiagDiagnosis,
  SiteAutodiagDiagnosisFailures,
} from "~domain/sites/models/site-autodiag-model.ts";
import type {
  Site,
  SiteCommon,
  SiteExternalId,
  SiteId,
  SiteLayoutId,
  SiteLayoutTypeId,
  SitePartnerId,
  SiteSubscribedPowerTypeId,
  SiteWithAddress,
  SiteWithPartialAddress,
} from "~domain/sites/models/site-model.ts";
import type { SearchSiteResult } from "~domain/sites/models/site-search-model.ts";
import type { SiteWorkingCondition } from "~domain/sites/models/site-working-condition.ts";
import type { SiteResponse } from "~infrastructure/.generated/hey/backoffice-backend";
import type { BackofficeFindAutodiagDiagnosisResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-autodiag-diagnosis-response.ts";
import type { BackofficeFindSiteModulatorsWorkingConditionResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-modulators-working-condition-response.ts";
import type { BackofficeSiteSearchResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-site-search-response.ts";
import type { DeepRequired } from "~utils/types.ts";

import { SubscriberMapper } from "~infrastructure/subscribers/mappers/subscriber-mapper.ts";

export const SiteMapper = {
  fromBackofficeSearchSiteToDomain(backofficeSearchSite: BackofficeSiteSearchResponse[]): SearchSiteResult[] {
    return backofficeSearchSite.map((item) => {
      return {
        modulator: {
          csId: item.modulatorSearchInfo.csId ?? undefined,
          hwId: item.modulatorSearchInfo.hwId ?? undefined,
          neuronId: item.modulatorSearchInfo.neuronId ?? undefined,
          serialNumber: item.modulatorSearchInfo.serialNumber ?? undefined,
        },
        pilot: {
          csId: item.pilotSearchInfo.csId ?? undefined,
          hwId: item.pilotSearchInfo.hwId ?? undefined,
        },
        subscriber: {
          companyName: item.subscriberSearchInfo?.companyName ?? undefined,
          emailList: item.subscriberSearchInfo?.emailList ?? [],
          phoneList: item.subscriberSearchInfo?.phoneList ?? [],
          firstname: item.subscriberSearchInfo?.firstname ?? undefined,
          lastname: item.subscriberSearchInfo?.lastname ?? undefined,
          id: item.subscriberSearchInfo?.id ?? undefined,
        },
        siteId: item.siteSearchInfo.id,
        deliveryPoint: item.siteSearchInfo.deliveryPoint ?? undefined,
        postalAddress: {
          address: item.siteSearchInfo.postalAddress?.address ?? undefined,
          addressComplement: item.siteSearchInfo.postalAddress?.addressComplement ?? undefined,
          digicode: item.siteSearchInfo.postalAddress?.digicode ?? undefined,
          building: item.siteSearchInfo.postalAddress?.building ?? undefined,
          level: item.siteSearchInfo.postalAddress?.level ?? undefined,
          stairway: item.siteSearchInfo.postalAddress?.stairway ?? undefined,
          cityCode: item.siteSearchInfo.postalAddress?.cityCode ?? undefined,
          zipCode: item.siteSearchInfo.postalAddress?.zipCode ?? undefined,
          city: item.siteSearchInfo.postalAddress?.city ?? undefined,
        },
        geocodedAddress: {
          administrativeDivision: item.siteSearchInfo.geocodedAddress?.administrativeDivision ?? [],
          cityCode: item.siteSearchInfo.geocodedAddress?.cityCode ?? undefined,
          country: item.siteSearchInfo.geocodedAddress?.country ?? undefined,
          geocoding: item.siteSearchInfo.geocodedAddress?.geocoding ?? undefined,
          inseeCode: item.siteSearchInfo.geocodedAddress?.inseeCode ?? undefined,
          label: item.siteSearchInfo.geocodedAddress?.label ?? undefined,
          street: item.siteSearchInfo.geocodedAddress?.street ?? undefined,
          streetNumber: item.siteSearchInfo.geocodedAddress?.streetNumber ?? undefined,
          city: item.siteSearchInfo.geocodedAddress?.city ?? undefined,
          zipCode: item.siteSearchInfo.geocodedAddress?.zipCode ?? undefined,
        },
      } satisfies SearchSiteResult;
    });
  },
  fromBackofficeSiteToDomain(backofficeSite: SiteResponse): Site {
    const siteCommon: SiteCommon = {
      siteId: backofficeSite.id as SiteId,
      surface: backofficeSite.surface ?? undefined,
      subscriber: SubscriberMapper.fromBackofficeSubscriberToDomain(backofficeSite.subscriber),
      phoningMission:
        backofficeSite.phoningMission === null
          ? undefined
          : {
              id: backofficeSite.phoningMission.id as MissionId,
              code: backofficeSite.phoningMission.code,
              note: backofficeSite.phoningMission.note,
            },
      subscribedPowerType: {
        id: backofficeSite.subscribedPowerType.id as SiteSubscribedPowerTypeId,
        description: backofficeSite.subscribedPowerType.description,
      },
      layout: !backofficeSite.layout
        ? undefined
        : {
            id: backofficeSite.layout.id as SiteLayoutId,
            description: backofficeSite.layout.description,
            type: {
              id: backofficeSite.layout.type.id as SiteLayoutTypeId,
              accommodation: backofficeSite.layout.type.accommodation,
              description: backofficeSite.layout.type.description,
            },
          },
      partner: !backofficeSite.partner
        ? undefined
        : {
            id: backofficeSite.partner.id as SitePartnerId,
            name: backofficeSite.partner.name,
          },
      showPartnerBadge: backofficeSite.showPartnerBadge,
      siteExternalId: backofficeSite.externId ? (backofficeSite.externId as SiteExternalId) : undefined,
    };

    if (backofficeSite.address) {
      return {
        ...siteCommon,
        address: backofficeSite.address,
        partialAddress: undefined,
      } satisfies SiteWithAddress;
    }

    // Supposedly a dead branch but the input types (from the OpenAPI) specs are not great.
    // Compiler does not seem to infer properly that partialAddress is always defined at this point.
    // (I did tell them that having a sum type at the "Site" level for a difference on 'address'  was a bad idea)
    if (backofficeSite.partialAddress === null) {
      console.error({ backofficeSite });
      throw new Error("Site should have either one of 'address' or 'partialAddress', not both at null");
    }

    return {
      ...siteCommon,
      address: undefined,
      partialAddress: backofficeSite.partialAddress,
    } satisfies SiteWithPartialAddress;
  },
  fromBackofficeSiteWorkingConditionToDomain(
    backofficeResponse: BackofficeFindSiteModulatorsWorkingConditionResponse,
  ): SiteWorkingCondition {
    const hasModulationPerformance = (
      r?: BackofficeFindSiteModulatorsWorkingConditionResponse[number]["linkModulationPerformance"] | null,
    ): r is DeepRequired<BackofficeFindSiteModulatorsWorkingConditionResponse[number]["linkModulationPerformance"]> =>
      !!r && r.modulationNumber !== null && r.modulationPerformancePercentage !== null;

    return backofficeResponse.map(
      (r) =>
        ({
          acqRate: {
            interpreted: r.acqRate.interpreted,
            raw: r.acqRate.raw,
            isECS: r.acqRate.isECS,
          },
          csLinkId: r.csLinkId,
          csModulatorId: r.csModulatorId,
          lastConnectionDate: r.lastConnectionDate ?? undefined,
          lastPointDate: r.lastPointDate ?? undefined,
          modulationEndDate: r.modulationEndDate ?? undefined,
          linkModulationPerformance: !hasModulationPerformance(r.linkModulationPerformance)
            ? undefined
            : {
                modulationPerformancePercentage: r.linkModulationPerformance.modulationPerformancePercentage,
                modulationNumber: r.linkModulationPerformance.modulationNumber,
              },
        }) satisfies SiteWorkingCondition[number],
    );
  },
  fromBackofficeAutodiagDiagnosisToDomain(
    backofficeAutodiagDiagnosis: BackofficeFindAutodiagDiagnosisResponse,
  ): SiteAutodiagDiagnosis {
    return {
      devices: backofficeAutodiagDiagnosis.devices.map(SiteMapper.fromBackofficeAutodiagDeviceDiagnosisToDomain),
      disabled: backofficeAutodiagDiagnosis.disableAutoDiagFeature,
      failures: SiteMapper.fromBackofficeAutodiagFailuresToDomain(backofficeAutodiagDiagnosis.failures),
      lastSync: backofficeAutodiagDiagnosis.lastSync ?? undefined,
    };
  },
  fromBackofficeAutodiagDeviceDiagnosisToDomain(
    deviceDiagnosis: BackofficeFindAutodiagDiagnosisResponse["devices"][number],
  ): SiteAutodiagDeviceDiagnosis {
    return {
      diagnostic: SiteMapper.fromBackofficeAutodiagDeviceDiagnosticToDomain(deviceDiagnosis.diagnostic),
      info: SiteMapper.fromBackofficeAutodiagDeviceInfoToDomain(deviceDiagnosis.info),
      memo: SiteMapper.fromBackofficeAutodiagDeviceMemoToDomain(deviceDiagnosis.memo),
      programming: SiteMapper.fromBackofficeAutodiagDeviceProgrammingToDomain(deviceDiagnosis.programming),
      sensor: SiteMapper.fromBackofficeAutodiagDeviceSensorToDomain(deviceDiagnosis.sensor),
    };
  },
  fromBackofficeAutodiagFailuresToDomain(
    backofficeFailures: BackofficeFindAutodiagDiagnosisResponse["failures"],
  ): SiteAutodiagDiagnosisFailures {
    return {
      failedToFetchMyVEnergyManager: backofficeFailures?.failedToFetchMyVEnergyManager ?? false,
    };
  },
  fromBackofficeAutodiagDeviceMemoToDomain(
    backofficeMemo: BackofficeFindAutodiagDiagnosisResponse["devices"][number]["memo"],
  ): SiteAutodiagDeviceMemo | undefined {
    if (!backofficeMemo) {
      return undefined;
    }

    return {
      ...backofficeMemo,
      content: backofficeMemo.content ?? undefined,
    };
  },
  fromBackofficeAutodiagDeviceProgrammingToDomain(
    backofficeProgramming: BackofficeFindAutodiagDiagnosisResponse["devices"][number]["programming"],
  ): SiteAutodiagDeviceProgramming | undefined {
    if (!backofficeProgramming) {
      return undefined;
    }

    return {
      ...backofficeProgramming,
      mode: backofficeProgramming.mode ?? undefined,
      type: backofficeProgramming.type ?? undefined,
    };
  },
  fromBackofficeAutodiagDeviceInfoToDomain(
    backofficeDeviceInfo: BackofficeFindAutodiagDiagnosisResponse["devices"][number]["info"],
  ): SiteAutodiagDeviceInfo | undefined {
    if (!backofficeDeviceInfo) {
      return undefined;
    }

    return {
      ...backofficeDeviceInfo,
      myVName: backofficeDeviceInfo.myVName ?? undefined,
      technicalName: backofficeDeviceInfo.technicalName ?? undefined,
      serial: backofficeDeviceInfo.serial ?? undefined,
      hasEnergyManager: backofficeDeviceInfo.hasEnergyManager ?? undefined,
    };
  },
  fromBackofficeAutodiagDeviceDiagnosticToDomain(
    backofficeDeviceDiagnostic: BackofficeFindAutodiagDiagnosisResponse["devices"][number]["diagnostic"],
  ): SiteAutodiagDeviceDiagnostic | undefined {
    if (!backofficeDeviceDiagnostic) {
      return undefined;
    }

    return {
      ...backofficeDeviceDiagnostic,
      start: backofficeDeviceDiagnostic.start ?? undefined,
      end: backofficeDeviceDiagnostic.end ?? undefined,
      result: backofficeDeviceDiagnostic.result ?? undefined,
    };
  },
  fromBackofficeAutodiagDeviceSensorToDomain(
    backofficeSensor: BackofficeFindAutodiagDiagnosisResponse["devices"][number]["sensor"],
  ): SiteAutodiagDeviceSensor | undefined {
    if (!backofficeSensor) {
      return undefined;
    }

    return {
      ...backofficeSensor,
      installationDatetime: backofficeSensor.installationDatetime ?? undefined,
      serial: backofficeSensor.serial ?? undefined,
    };
  },
};
